import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Container, Spinner } from 'react-bootstrap';
import { useCrypto } from '../../components/CryptoContext.tsx';
import { recoveryApi, type RecoveryApiClient } from '../../recovery/api.ts';
import { createRecoveryCryptoBridge, type RecoveryCryptoBridge } from '../../recovery/cryptoBridge.ts';
import type { LocalCryptoHealth, RecoveryOverview } from '../../recovery/types.ts';
import RecoveryCenter from './RecoveryCenter.tsx';

export default function RecoveryPage() {
  const { exportAdultVaultKey, installRecoveredAdultVault, status } = useCrypto();
  const exportAdultVaultKeyRef = useRef(exportAdultVaultKey);
  const installRecoveredAdultVaultRef = useRef(installRecoveredAdultVault);
  exportAdultVaultKeyRef.current = exportAdultVaultKey;
  installRecoveredAdultVaultRef.current = installRecoveredAdultVault;

  const [bridge, setBridge] = useState<RecoveryCryptoBridge | null>(null);
  const [health, setHealth] = useState<LocalCryptoHealth | null>(null);
  const [bootError, setBootError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void createRecoveryCryptoBridge({
      apiClient: recoveryApi,
      adultVault: {
        exportKey: (ownerUserId) => exportAdultVaultKeyRef.current(ownerUserId),
        installKey: (ownerUserId, vaultKey) =>
          installRecoveredAdultVaultRef.current(ownerUserId, vaultKey),
      },
    })
      .then((created) => {
        if (cancelled) return;
        setBridge(created);
        setHealth(created.health);
      })
      .catch((cause) => {
        if (cancelled) return;
        setBootError(
          cause instanceof Error
            ? cause.message
            : 'Local recovery crypto could not start.'
        );
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const apiClient = useMemo<RecoveryApiClient>(() => {
    return {
      ...recoveryApi,
      async getOverview(signal) {
        const overview = await recoveryApi.getOverview(signal);
        if (!bridge) return overview;
        const inspection = await bridge.inspectLocalKeys({
          membershipIds: overview.assignments.map((assignment) => assignment.id),
          deviceFingerprints: overview.devices
            .map((device) => device.fingerprint)
            .filter((value): value is string => Boolean(value)),
        });
        setHealth({
          ...bridge.health,
          ...inspection,
        });
        return {
          ...overview,
          assignments: overview.assignments.map((assignment) => ({
            ...assignment,
            localKeyPresent: !inspection.missingMembershipIds.includes(
              assignment.id
            ),
          })),
        } satisfies RecoveryOverview;
      },
    };
  }, [bridge]);

  if (bootError) {
    return (
      <div className="main-content recovery-center">
        <Container className="my-4">
          <h1 className="display-6">Recovery circle</h1>
          <Alert variant="danger">{bootError}</Alert>
        </Container>
      </div>
    );
  }

  if (!bridge || !health) {
    return (
      <div className="main-content recovery-center">
        <Container className="my-4">
          <div className="recovery-page-state" role="status" aria-live="polite">
            <Spinner animation="border" aria-hidden="true" />
            <p>Preparing local recovery crypto…</p>
          </div>
        </Container>
      </div>
    );
  }

  return (
    <RecoveryCenter
      apiClient={apiClient}
      prepareCircleActivation={bridge.prepareCircleActivation}
      getMembershipCustodyPublicKeyJwk={bridge.getMembershipCustodyPublicKeyJwk}
      getDeviceCustodyPublicKeyJwk={bridge.getDeviceCustodyPublicKeyJwk}
      getRecoveryTargetPublicKeyJwk={bridge.getRecoveryTargetPublicKeyJwk}
      getRequestBoundSealedShare={bridge.getRequestBoundSealedShare}
      completeRecoveryOnTarget={bridge.completeRecoveryOnTarget}
      retireCircle={(circle) => recoveryApi.retireCircle(circle.id)}
      onDeviceRevoked={(device) => bridge.afterDeviceRevoked(device)}
      localCryptoHealth={health}
      chatIdentityUnlocked={status === 'unlocked'}
    />
  );
}
